import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { Check, X, AlertTriangle, Loader2, Building2, Zap } from 'lucide-react'
import { CopyButton, RawJsonCollapsible, ComboSelect, inputCls, labelCls, SectionLabel, ResultBadge } from '../styles'
import { KYB_SCENARIOS } from '@features/dashboards/testing-ground/data'
import { proxyUrl, buildHeaders, outcomeColors } from '@features/dashboards/testing-ground/utils'
import type { Creds, LogEntry } from '@features/dashboards/testing-ground/types'
import { outcomeLabel } from '../utils'
import { ConfiguringChecklist } from './ConfiguringChecklist'
import { InteractionDiagram } from './InteractionDiagram'
import { KybConfigPanel } from './KybConfigPanel'
import type { KybDiagramConfig, KybAiSuggestion } from './KybConfigPanel'
import { KybGuidedView } from './KybGuidedView'
import { ExperienceModeSwitcher } from './ExperienceModeSwitcher'
import type { ExperienceMode } from './ExperienceModeSwitcher'
import { ExistingCustomerPanel } from './ExistingCustomerPanel'
import type { ExistingCustomerSelection } from './ExistingCustomerPanel'
import type { CustomerMode } from './NewExistingToggle'
import { StageSaveBar } from './StageSaveBar'
import { CONFIG_CHECKLISTS, DEFAULT_KYB_AU_CHECKS, DEFAULT_KYB_OWNERSHIP } from '../data'
import { buildKybDiagramDsl } from '../dynamicInteractionDiagrams'
import type { PlatformJourneyRecord } from '@api/client'
import type { AnyObj } from '../types'
import type { AiConfigureSuggestions, AiStageSuggestionState } from '../aiConfigure'
import { AiSuggestionBanner } from './AiSuggestionBanner'

const DEFAULT_DIAGRAM_CONFIG: KybDiagramConfig = {
  auChecks: DEFAULT_KYB_AU_CHECKS,
  ownership: DEFAULT_KYB_OWNERSHIP,
}

interface KybStageProps {
  creds: Creds
  hasCreds: boolean
  addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void
  onRunningChange: (running: boolean) => void
  onComplete: (workflowResult: AnyObj | null) => void
  customerMode: CustomerMode
  existingCustomer: ExistingCustomerSelection | null
  customerName: string | null
  platformVersion: 'v1' | 'v2'
  experienceMode: ExperienceMode
  onExperienceModeChange: (mode: ExperienceMode) => void
  journeyId: number | null
  journeyRecord: PlatformJourneyRecord | null
  onJourneySaved: () => void
  aiSuggestions?: AiConfigureSuggestions | null
  /** Lifted to the tool shell so Account Setup's checklist and this stage's
   * own banner share one apply/dismiss state instead of drifting apart. */
  kybAi: AiStageSuggestionState<KybAiSuggestion>
}

export function KybStage({
  creds, hasCreds, addLog, onRunningChange, onComplete,
  customerMode, existingCustomer, customerName, platformVersion,
  experienceMode, onExperienceModeChange, journeyId, journeyRecord, onJourneySaved,
  aiSuggestions, kybAi,
}: KybStageProps) {
  const [workflows, setWorkflows] = useState<{ serviceName: string; workflowName: string }[]>([])
  const [selectedWorkflow, setSelectedWorkflow] = useState('')
  const [kybServiceName, setKybServiceName] = useState('KYB')
  const [diagramConfig, setDiagramConfig] = useState<KybDiagramConfig>(DEFAULT_DIAGRAM_CONFIG)
  const [checklistEnabled, setChecklistEnabled] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(CONFIG_CHECKLISTS.kyb.map((i) => [i.id, true]))
  )

  useEffect(() => {
    if (!journeyRecord) return
    if (journeyRecord.kybConfig) {
      const cfg = journeyRecord.kybConfig as { selectedWorkflow?: string; kybServiceName?: string; diagramConfig?: KybDiagramConfig }
      if (cfg.selectedWorkflow) setSelectedWorkflow(cfg.selectedWorkflow)
      if (cfg.kybServiceName) setKybServiceName(cfg.kybServiceName)
      if (cfg.diagramConfig) setDiagramConfig(cfg.diagramConfig)
    }
    if (journeyRecord.kybChecklist) setChecklistEnabled(journeyRecord.kybChecklist as Record<string, boolean>)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyRecord?.id])

  const [scenarioIdx, setScenarioIdx] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [pollAttempt, setPollAttempt] = useState(0)
  const [entityId, setEntityId] = useState<string | null>(null)
  const [workflowResult, setWorkflowResult] = useState<AnyObj | null>(null)
  const [resultOrg, setResultOrg] = useState<AnyObj | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)
  const [notFound, setNotFound] = useState(false)

  function toggleChecklistItem(id: string) {
    setChecklistEnabled((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const doFetch = useCallback(async (method: string, path: string, body: unknown | undefined, annotation: string) => {
    const url = `${creds.apiBase}${path}`
    const start = Date.now()
    const res = await fetch(proxyUrl(path), {
      method,
      headers: buildHeaders(creds, method !== 'GET'),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
    const durationMs = Date.now() - start
    let data: unknown
    try { data = await res.json() } catch { data = {} }
    addLog({ method, url, status: res.status, durationMs, requestBody: body, responseBody: data, annotation })
    if (!res.ok) throw new Error((data as { error?: string })?.error ?? res.statusText)
    return data
  }, [creds, addLog])

  const loadWorkflows = useCallback(async () => {
    try {
      const data = await doFetch('GET', '/workflows', undefined, 'List configured workflows') as { workflows?: { serviceName: string; workflowName: string }[] }
      const all = data?.workflows ?? []
      const kyb = all.filter((w) => w.serviceName?.toUpperCase().includes('KYB'))
      const toShow = kyb.length > 0 ? kyb : all
      setWorkflows(toShow)
      if (toShow.length > 0) {
        setSelectedWorkflow(toShow[0].workflowName)
        setKybServiceName(toShow[0].serviceName ?? 'KYB')
      }
    } catch { /* logged */ }
  }, [doFetch])

  const lastAutoLoadedCustomer = useRef('')
  useEffect(() => {
    if (hasCreds && creds.customerId !== lastAutoLoadedCustomer.current) {
      lastAutoLoadedCustomer.current = creds.customerId
      void loadWorkflows()
    }
  }, [hasCreds, creds.customerId, loadWorkflows])

  async function pollResult(eid: string, weid: string, wf: string, serviceName: string) {
    for (let i = 0; i < 20; i++) {
      setPollAttempt(i + 1)
      try {
        const data = await doFetch(
          'GET',
          `/organizations/${eid}/serviceprofiles/${serviceName}/workflows/${wf}/executions/${weid}`,
          undefined,
          `Poll KYB result (attempt ${i + 1}/20)`,
        ) as { workflowResult?: AnyObj; organization?: AnyObj }
        const state = data?.workflowResult?.workflowExecutionState
        if (state === 'COMPLETED' || state === 'ERROR' || state === 'TIMEOUT') {
          setWorkflowResult(data.workflowResult ?? null)
          onComplete(data.workflowResult ?? null)
          setResultOrg(data.organization ?? null)
          setFullResult(data)
          return
        }
      } catch { /* keep polling */ }
      await new Promise((r) => setTimeout(r, 4000))
    }
    setPollAttempt(0)
  }

  async function runKybVerification() {
    if (!selectedWorkflow || scenarioIdx === null) return
    const persona = KYB_SCENARIOS[scenarioIdx]
    const wf = selectedWorkflow
    const serviceName = kybServiceName
    setLoading(true)
    onRunningChange(true)
    setNotFound(false)
    setWorkflowResult(null)
    onComplete(null)
    setResultOrg(null)
    setFullResult(null)
    setEntityId(null)
    setPollAttempt(0)
    try {
      const lookupData = await doFetch(
        'POST',
        '/organizations/lookup',
        { organizationNumber: { registrationNumber: persona.searchValue, registryCode: persona.searchRegType }, region: { country: 'AU' } },
        `Search business registry (ABR/ASIC) for ${persona.name}`,
      ) as { matchedOrganizations?: AnyObj[] }
      const match = lookupData?.matchedOrganizations?.[0]
      if (!match) {
        setNotFound(true)
        return
      }
      const execData = await doFetch(
        'POST',
        `/organizations/workflows/${wf}/execute`,
        { organizationToken: match.organizationToken },
        'Execute KYB workflow by token — creates the organisation entity and runs verification in one call',
      ) as { entityId?: string; workflowExecutionId?: string; workflowResult?: AnyObj }
      const eid = execData?.entityId
      if (eid) setEntityId(eid)
      const state = execData?.workflowResult?.workflowExecutionState
      if (state === 'COMPLETED' || state === 'ERROR' || state === 'TIMEOUT') {
        setWorkflowResult(execData.workflowResult ?? null)
        onComplete(execData.workflowResult ?? null)
        setFullResult(execData)
      } else if (execData?.workflowExecutionId && eid) {
        await pollResult(eid, execData.workflowExecutionId, wf, serviceName)
      }
    } catch { /* logged */ } finally {
      setLoading(false)
      onRunningChange(false)
      setPollAttempt(0)
    }
  }

  const outcome = workflowResult ? outcomeLabel(workflowResult) : ''
  const oc = outcomeColors(outcome)
  const diagramDsl = useMemo(
    () => buildKybDiagramDsl({ platformVersion, workflowName: selectedWorkflow, ...diagramConfig }),
    [platformVersion, selectedWorkflow, diagramConfig],
  )

  const ubos: AnyObj[] = Array.isArray(resultOrg?.ultimateBeneficialOwners) ? resultOrg.ultimateBeneficialOwners : []
  const officials: AnyObj[] = Array.isArray(resultOrg?.officials) ? resultOrg.officials : []
  const legalName: string | null = resultOrg?.details?.name?.name ?? resultOrg?.name?.name ?? null

  const kybAiBanner = kybAi.visible && aiSuggestions?.kyb && (
    <AiSuggestionBanner
      rationale={aiSuggestions.kyb.rationale}
      confidence={aiSuggestions.kyb.confidence}
      applied={kybAi.applied}
      onApply={kybAi.apply}
      onDismiss={kybAi.dismiss}
    />
  )

  const technicalDetailsNode = (
    <>
      <div>
        <label className={labelCls}>Workflow</label>
        <ComboSelect
          value={selectedWorkflow}
          onChange={setSelectedWorkflow}
          options={workflows.map((w) => ({ value: w.workflowName }))}
          placeholder="Workflow name"
          className={inputCls}
        />
      </div>
      {kybAiBanner}
      <KybConfigPanel onDiagramConfigChange={setDiagramConfig} aiSeed={kybAi.seed} />
      <InteractionDiagram stageId="kyb" dsl={diagramDsl} />
      {entityId && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.2)' }}>
          <span className="text-[10px] font-semibold text-emerald-700">entityId</span>
          <span className="text-[10px] font-mono text-[#475569] flex-1 truncate">{entityId}</span>
          <CopyButton text={entityId} />
        </div>
      )}
      {(ubos.length > 0 || officials.length > 0) && (
        <div className="rounded-[11px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
          <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">Ownership — UBOs & Officials</p>
          </div>
          <div className="divide-y" style={{ borderColor: '#e5e7eb' }}>
            {ubos.map((u, i) => (
              <div key={`ubo-${i}`} className="flex items-center gap-3 px-4 py-2">
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0" style={{ background: 'rgba(129,140,248,.15)', color: '#4f46e5' }}>UBO</span>
                <span className="text-[11px] text-[#475569] flex-1 truncate">{u.name?.displayName ?? (`${u.name?.givenName ?? ''} ${u.name?.familyName ?? ''}`.trim() || '—')}</span>
                {u.ownershipPercentage !== undefined && <span className="text-[10px] font-mono text-[#64748b]">{u.ownershipPercentage}%</span>}
              </div>
            ))}
            {officials.map((o, i) => (
              <div key={`official-${i}`} className="flex items-center gap-3 px-4 py-2">
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0" style={{ background: 'rgba(148,163,184,.15)', color: '#475569' }}>OFFICIAL</span>
                <span className="text-[11px] text-[#475569] flex-1 truncate">{o.name?.displayName ?? (`${o.name?.givenName ?? ''} ${o.name?.familyName ?? ''}`.trim() || '—')}</span>
                {o.role && <span className="text-[10px] font-mono text-[#64748b]">{String(o.role)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {workflowResult?.riskAssessment && (
        <div className="rounded-[11px] overflow-hidden p-3 flex items-center gap-2" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
          <ResultBadge result={workflowResult.riskAssessment.riskLevel ?? '—'} />
          <span className="text-[10px] text-[#64748b]">Risk score {workflowResult.riskAssessment.riskScore ?? '—'}</span>
        </div>
      )}
      <RawJsonCollapsible data={fullResult} />
    </>
  )

  return (
    <div className="space-y-3">
      {customerMode === 'existing' ? (
        <div className="space-y-3">
          <ExistingCustomerPanel
            productKey="kyb"
            sections={['KYB']}
            title="KYB — Existing Customer"
            description="Shows this customer's KYB (Know Your Business) settings as scoped in DealHub. Reads DealHub's own presales record, not FrankieOne's live platform."
            showCascadeSummary={false}
            sharedSelection={existingCustomer}
            searchable={false}
          />
          {kybAiBanner}
          <KybConfigPanel onDiagramConfigChange={setDiagramConfig} aiSeed={kybAi.seed} />
        </div>
      ) : (
      <>
      <ExperienceModeSwitcher mode={experienceMode} onChange={onExperienceModeChange} />
      <StageSaveBar
        journeyId={journeyId}
        buildFields={() => ({ kybConfig: { selectedWorkflow, kybServiceName, diagramConfig }, kybChecklist: checklistEnabled })}
        onSaved={onJourneySaved}
      />

      {experienceMode === 'guided' && (
        <KybGuidedView
          hasCreds={hasCreds}
          loading={loading}
          pollAttempt={pollAttempt}
          scenarioIdx={scenarioIdx}
          onSelectPersona={(i) => { setScenarioIdx(i); setWorkflowResult(null); setNotFound(false) }}
          onRun={() => void runKybVerification()}
          canRun={!loading && hasCreds && !!selectedWorkflow && scenarioIdx !== null}
          notFound={notFound}
          workflowResult={workflowResult}
          outcome={outcome}
          legalName={legalName}
          ownerCount={ubos.length + officials.length}
          customerName={customerName}
          checklistItems={CONFIG_CHECKLISTS.kyb}
          checklistEnabled={checklistEnabled}
          onToggleChecklistItem={toggleChecklistItem}
          technicalDetails={technicalDetailsNode}
        />
      )}

      {experienceMode === 'classic' && (
      <>
      <ConfiguringChecklist productLabel="KYB" items={CONFIG_CHECKLISTS.kyb} enabled={checklistEnabled} onToggle={toggleChecklistItem} customerName={customerName} />
      {kybAiBanner}
      <KybConfigPanel onDiagramConfigChange={setDiagramConfig} aiSeed={kybAi.seed} />
      <InteractionDiagram stageId="kyb" dsl={diagramDsl} />

      <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(320px, 380px) 1fr' }}>
        <div className="rounded-[14px] overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
          <div className="px-4 py-3" style={{ background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
            <p className="text-[12px] font-semibold text-[#1e293b]">KYB</p>
            <p className="text-[10px] text-[#64748b] mt-0.5">Looks up a real registered business, then creates + verifies it in one workflow execution</p>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className={labelCls}>Workflow</label>
              <ComboSelect
                value={selectedWorkflow}
                onChange={setSelectedWorkflow}
                options={workflows.map((w) => ({ value: w.workflowName }))}
                placeholder="Workflow name"
                className={inputCls}
              />
            </div>

            <div>
              <SectionLabel>Test Business</SectionLabel>
              <div className="flex flex-col gap-1.5">
                {KYB_SCENARIOS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => { setScenarioIdx(i); setWorkflowResult(null); setNotFound(false) }}
                    className="flex items-center gap-2 px-2.5 py-[7px] rounded-[8px] text-[11px] font-medium text-left transition-all"
                    style={scenarioIdx === i
                      ? { background: 'rgba(99,102,241,.16)', border: '1px solid rgba(99,102,241,.45)', color: '#1e293b' }
                      : { background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569' }
                    }
                  >
                    <Building2 size={11} className="shrink-0" />
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => void runKybVerification()}
              disabled={loading || !hasCreds || !selectedWorkflow || scenarioIdx === null}
              className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
              style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
            >
              {loading
                ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Running…</span>
                : 'Run KYB Verification'}
            </button>
            {!hasCreds && <p className="text-[10px] text-amber-700 text-center">Configure credentials above to make live calls</p>}
            {scenarioIdx === null && hasCreds && (
              <p className="text-[10px] text-amber-700 text-center">Pick a test business first</p>
            )}

            {entityId && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.2)' }}>
                <span className="text-[10px] font-semibold text-emerald-700">entityId</span>
                <span className="text-[10px] font-mono text-[#475569] flex-1 truncate">{entityId}</span>
                <CopyButton text={entityId} />
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3 min-w-0">
          {loading && (
            <div className="p-5 rounded-[14px] space-y-4" style={{ background: '#ffffff', border: '1px solid #e5e7eb' }}>
              <div className="flex items-center gap-3">
                <Loader2 size={18} className="animate-spin text-amber-700 shrink-0" />
                <p className="text-[13px] font-semibold text-[#b45309]">
                  {pollAttempt === 0 ? 'Looking up business & running workflow…' : `Polling for result… attempt ${pollAttempt}/20`}
                </p>
              </div>
              {pollAttempt > 0 && (
                <div className="h-[5px] rounded-full" style={{ background: '#f1f5f9' }}>
                  <div className="h-full rounded-full transition-all duration-300" style={{ width: `${Math.min((pollAttempt / 20) * 100, 100)}%`, background: 'linear-gradient(90deg, #b45309, #b45309)' }} />
                </div>
              )}
            </div>
          )}

          {!loading && notFound && (
            <div className="p-4 rounded-[14px] flex items-start gap-2" style={{ background: 'rgba(251,146,60,.06)', border: '1px solid rgba(251,146,60,.25)' }}>
              <AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-700">The registry lookup returned no match for this business — try a different test business or re-run.</p>
            </div>
          )}

          {!loading && !workflowResult && !notFound && (
            <div className="flex flex-col items-center justify-center py-10 rounded-[14px]" style={{ border: '2px dashed #e5e7eb', background: '#f8fafc' }}>
              <div className="w-12 h-12 rounded-[12px] flex items-center justify-center mb-4" style={{ background: 'rgba(99,102,241,.12)', border: '1px solid rgba(99,102,241,.2)' }}>
                <Zap size={22} className="text-[#6366f1]" />
              </div>
              <p className="text-[14px] font-semibold text-[#64748b]">Run a KYB verification to see the result</p>
            </div>
          )}

          {!loading && workflowResult && (
            <div className="space-y-3" style={{ animation: 'fadeUp .25s ease' }}>
              <div className="p-4 rounded-[14px]" style={{ background: oc.bg, border: `1px solid ${oc.border}` }}>
                <div className="flex items-start gap-4">
                  <div className="w-[46px] h-[46px] rounded-[12px] flex items-center justify-center shrink-0" style={{ background: `${oc.icon}20`, border: `1px solid ${oc.border}` }}>
                    {outcome === 'PASS' && <Check size={22} style={{ color: oc.icon }} />}
                    {outcome === 'FAIL' && <X size={22} style={{ color: oc.icon }} />}
                    {outcome === 'REFER' && <AlertTriangle size={22} style={{ color: oc.icon }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[19px] font-extrabold" style={{ color: oc.text }}>{outcome}</span>
                    {legalName && <p className="text-[11px] text-[#475569] mt-1">{String(legalName)}</p>}
                  </div>
                </div>
              </div>

              {(ubos.length > 0 || officials.length > 0) && (
                <div className="rounded-[11px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
                  <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">Ownership — UBOs & Officials</p>
                  </div>
                  <div className="divide-y" style={{ borderColor: '#e5e7eb' }}>
                    {ubos.map((u, i) => (
                      <div key={`ubo-${i}`} className="flex items-center gap-3 px-4 py-2">
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0" style={{ background: 'rgba(129,140,248,.15)', color: '#4f46e5' }}>UBO</span>
                        <span className="text-[11px] text-[#475569] flex-1 truncate">{u.name?.displayName ?? (`${u.name?.givenName ?? ''} ${u.name?.familyName ?? ''}`.trim() || '—')}</span>
                        {u.ownershipPercentage !== undefined && <span className="text-[10px] font-mono text-[#64748b]">{u.ownershipPercentage}%</span>}
                      </div>
                    ))}
                    {officials.map((o, i) => (
                      <div key={`official-${i}`} className="flex items-center gap-3 px-4 py-2">
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0" style={{ background: 'rgba(148,163,184,.15)', color: '#475569' }}>OFFICIAL</span>
                        <span className="text-[11px] text-[#475569] flex-1 truncate">{o.name?.displayName ?? (`${o.name?.givenName ?? ''} ${o.name?.familyName ?? ''}`.trim() || '—')}</span>
                        {o.role && <span className="text-[10px] font-mono text-[#64748b]">{String(o.role)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {workflowResult.riskAssessment && (
                <div className="rounded-[11px] overflow-hidden p-3 flex items-center gap-2" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
                  <ResultBadge result={workflowResult.riskAssessment.riskLevel ?? '—'} />
                  <span className="text-[10px] text-[#64748b]">Risk score {workflowResult.riskAssessment.riskScore ?? '—'}</span>
                </div>
              )}

              <RawJsonCollapsible data={fullResult} />
            </div>
          )}
        </div>
      </div>
      </>
      )}
      </>
      )}
    </div>
  )
}
